import {styled as linariaStyled} from '@linaria/react';
import React from 'react';
import sc, {css} from 'styled-components';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../constants/typography';
import {Icon, IconSize} from '../../../atoms/Icon/Icon';
import {IconName} from '../../../atoms/Icon/IconName';
import {Image} from '../../../atoms/Image/Image';
import {badgeColors} from './badgeColors';

const IMG_ICON_SIZE = 12;

export interface BadgeProps {
  text: string | React.ReactNode;
  emoji?: string;
  className?: string;
  disableRoundedCorners?: boolean;
  withTextEllipsis?: boolean;
  dataTestId?: string;
  textColor?: string;
  backgroundColor?: string;
  imgIcon?: {
    src: string;
    alt: string;
  };
  svgIcon?: {
    iconName: IconName;
    iconSize: keyof typeof IconSize;
    fill?: string;
    showToTheRight?: boolean;
  };
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  increaseRightSideBorderRadius?: boolean;
  untranslatedLabel?: string;
}

export function Badge({
  text,
  emoji,
  className,
  disableRoundedCorners = false,
  dataTestId,
  textColor,
  backgroundColor,
  imgIcon,
  svgIcon,
  onClick,
  increaseRightSideBorderRadius,
  untranslatedLabel,
}: BadgeProps) {
  return (
    <BadgeContainer
      data-testid={dataTestId}
      data-untranslated-label={untranslatedLabel}
      className={className}
      disableRoundedCorners={disableRoundedCorners}
      $increaseRightSideBorderRadius={increaseRightSideBorderRadius}
      textColor={textColor}
      backgroundColor={backgroundColor}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      {emoji ? <span role="img">{emoji}</span> : null}
      {imgIcon ? (
        <StyledImgIcon
          breakpoints={[]}
          ratio="1:1"
          widths={[24]}
          src={imgIcon.src}
          alt={imgIcon.alt}
        />
      ) : null}
      {svgIcon ? (
        <StyledIcon
          asset={svgIcon.iconName}
          size={svgIcon.iconSize}
          fill={svgIcon.fill}
          $showToTheRight={svgIcon.showToTheRight}
        />
      ) : null}
      <span>{text}</span>
    </BadgeContainer>
  );
}

export const BadgeWithBorder = sc(Badge)<{borderColor?: string}>`
  && {
    border: 1px solid
      ${({borderColor}) => borderColor || badgeColors.colors.default};
    border-radius: 500px;
    background-color: transparent;
  }
`;

export const BadgeWithBorderRadius = sc(Badge)`
  && {
    border-radius: 500px;
  }
`;

export const BadgeContainer = linariaStyled.div<{
  disableRoundedCorners: boolean;
  textColor?: string;
  backgroundColor?: string;
  $withTextEllipsis?: boolean;
  $increaseRightSideBorderRadius?: boolean;
}>`
  height: 20px;
  width: fit-content;
  max-width: 100%;
  display: inline-flex;
  align-items: center;

  margin-top: 1px;
  margin-right: 5px;
  background-color: ${({backgroundColor}) =>
    backgroundColor || badgeColors.backgroundColors.default};
  color: ${({textColor}) => textColor || badgeColors.colors.default};
  border-radius: ${({
    disableRoundedCorners,
    $increaseRightSideBorderRadius,
  }) => {
    if (disableRoundedCorners) {
      return $increaseRightSideBorderRadius ? '0 4px 4px 0' : '0 2px 2px 0';
    } else {
      return '2px';
    }
  }};

  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  padding: ${({disableRoundedCorners}) =>
    disableRoundedCorners ? '2px 8px 2px 16px' : '2px 8px'};

  direction: initial;
  white-space: nowrap;
  gap: 5px;

  &:last-of-type {
    margin-right: 0;
  }

  > span {
    display: block;
    box-sizing: border-box;
    overflow: hidden;
    line-height: 1.3;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const StyledImgIcon = sc(Image)`
  width: ${IMG_ICON_SIZE}px;
  height: ${IMG_ICON_SIZE}px;
`;

const StyledIcon = sc(Icon)<{$showToTheRight?: boolean}>`
  ${({$showToTheRight}) =>
    $showToTheRight &&
    css`
      order: 4;
    `}
`;
